import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { V2_BASE } from '../config';
import {
  Home, ChevronRight, Globe, Server, LayoutTemplate, Plus,
  Search, SlidersHorizontal, Download, LayoutGrid, MoreVertical, ShoppingCart,
  ChevronDown, Lock, FolderOpen, Database, RotateCcw, ShieldCheck,
  ExternalLink, HardDrive, Calendar, Layers, Info, ArrowUpDown, ArrowRight, Users2,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { EMPTY_ILLU } from '../illu';
import './contabilita.css';
import './hosting.css';

const api = axios.create({ withCredentials: true });
const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }); } catch { return '—'; } };
const fmtBytes = (b) => {
  const n = Number(b);
  if (!isFinite(n) || n < 0) return '—';
  if (n === 0) return '0 B';
  const un = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(un.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${un[i]}`;
};
const STATE_TAG = (raw) => {
  const s = (raw || '').toLowerCase();
  if (['attivo', 'active', 'ok', 'valido'].includes(s)) return { cls: 'ok', label: 'Attivo' };
  if (['scaduto', 'scaduta', 'sospeso', 'expired', 'errore'].includes(s)) return { cls: 'danger', label: raw };
  if (['in_attesa', 'pending', 'in_lavorazione'].includes(s)) return { cls: 'warn', label: 'In attesa' };
  return { cls: 'info', label: raw || '—' };
};

/* Icona SVG NeoFlux (public/nav) */
const NavIcon = ({ name, size = 22 }) => (
  <img className="nfx-nav-ico" src={`/nav/${name}.svg`} alt="" aria-hidden="true" width={size} height={size} />
);
/* App installata su un sito (integrazione Webuzo): WordPress se rilevato, altrimenti Site Creator */
const appIcon = (h) => (/wordpress|word\s*press/i.test(`${h.piano || ''} ${h.dominio_principale || ''}`) ? 'nav-wordpress' : 'nav-site-creator');

const useFetch = (url, fallback) => {
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true; setLoading(true);
    api.get(url).then(r => { if (alive) setData(r.data); }).catch(() => { if (alive) setData(fallback); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [url]); // eslint-disable-line
  return [data, loading];
};

const TABS = [
  { key: 'siti',    label: 'Sito web',       path: '/hosting-domains/siti' },
  { key: 'hosting', label: 'Hosting',        path: '/hosting-domains/hosting' },
  { key: 'domains', label: 'Domini',         path: '/hosting-domains/domains' },
  { key: 'ssl',     label: 'Certificato SSL',path: '/hosting-domains/ssl' },
];

const IlluEmpty = ({ title, desc, primary, link, onPrimary, onLink }) => (
  <div className="nfx-card">
    <div className="nfx-hd-illu">
      <div className="nfx-hd-illu__l">
        <div className="nfx-hd-illu__title">{title}</div>
        <div className="nfx-hd-illu__desc">{desc}</div>
        <div className="nfx-hd-illu__actions">
          <button className="nfx-btn-blue" onClick={onPrimary}>{primary}</button>
          {link && <button className="nfx-hd-illu__link" onClick={onLink}>{link}</button>}
        </div>
      </div>
      <div className="nfx-hd-illu__illu"><img src={EMPTY_ILLU} alt="" /></div>
    </div>
  </div>
);

const HostingDashboard = ({ tab }) => {
  const navigate = useNavigate();
  const go = (p) => navigate(V2_BASE + p);
  const offer = () => window.open('https://neoflux.it', '_blank');
  const { utente } = useAuth();
  const [hosting, loadingH] = useFetch('/api/hosting', []);
  const [domini, loadingD] = useFetch('/api/domini', []);
  const hostList = Array.isArray(hosting) ? hosting : [];
  const domList = Array.isArray(domini) ? domini : [];

  // Titolare (proprietario dei domini = cliente loggato)
  const owner = (() => {
    const n = utente?.nome || '', c = utente?.cognome || '', rs = utente?.ragione_sociale;
    const full = [n, c].filter(Boolean).join(' ') || utente?.email || '—';
    if (rs) return `${n}${c ? ` ${c[0]}.` : ''}, ${rs}`;
    return full;
  })();
  const dMinus = (scad) => (scad ? Math.ceil((new Date(scad) - Date.now()) / 86400000) : null);

  // Selezione domini + batch bar
  const [selDom, setSelDom] = useState(() => new Set());
  const toggleSel = (id) => setSelDom((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSel = domList.length > 0 && domList.every((d) => selDom.has(d.id));
  const toggleAllDom = () => setSelDom(allSel ? new Set() : new Set(domList.map((d) => d.id)));

  // Azioni in blocco
  const [batchBusy, setBatchBusy] = useState('');
  const [batchMsg, setBatchMsg] = useState(null);
  const [nsModal, setNsModal] = useState(false);
  const [nsForm, setNsForm] = useState({ ns1: '', ns2: '', ns3: '', ns4: '', ns5: '' });
  const selNomi = () => domList.filter((d) => selDom.has(d.id)).map((d) => d.nome);
  const batchFlash = (type, text) => { setBatchMsg({ type, text }); setTimeout(() => setBatchMsg(null), 5000); };
  const runBatch = async (fn, kind, label) => {
    const nomi = selNomi(); if (!nomi.length) return;
    setBatchBusy(kind);
    const res = await Promise.allSettled(nomi.map((n) => fn(n)));
    const ok = res.filter((r) => r.status === 'fulfilled').length;
    setBatchBusy('');
    batchFlash(ok === nomi.length ? 'ok' : 'err', `${label}: ${ok}/${nomi.length} riusciti.`);
    if (ok) setSelDom(new Set());
  };
  const batchLock = () => runBatch((n) => api.put(`/api/domini/${encodeURIComponent(n)}/lock`, { locked: true }), 'lock', 'Blocco domini');
  const batchNs = () => {
    if (!nsForm.ns1 || !nsForm.ns2) { batchFlash('err', 'Inserisci almeno i primi due server DNS'); return; }
    setNsModal(false);
    runBatch((n) => api.put(`/api/domini/${encodeURIComponent(n)}/nameservers`, nsForm), 'ns', 'Modifica server DNS');
  };

  const rightIcons = (
    <div className="nfx-tb__actions">
      <button className="nfx-iconbtn"><Search size={18} /></button>
      <button className="nfx-iconbtn"><SlidersHorizontal size={18} /></button>
      <button className="nfx-iconbtn"><Download size={18} /></button>
      <button className="nfx-iconbtn is-active"><LayoutGrid size={18} /></button>
    </div>
  );

  return (
    <div>
      {/* Breadcrumb */}
      <div className="nfx-crumb">
        <button className="nfx-crumb__home" onClick={() => go('')} aria-label="Home"><Home size={16} /></button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <span className="nfx-crumb__cur"><Globe size={15} style={{ verticalAlign: '-2px', marginRight: 6, color: '#1d3c51' }} />Hosting e Domini</span>
      </div>
      <h1 className="nfx-cb-title">Dashboard Hosting e Domini</h1>

      {/* Tabs */}
      <div className="nfx-hdtabs">
        {TABS.map(t => (
          <button key={t.key} className={`nfx-hdtab${tab === t.key ? ' is-active' : ''}`} onClick={() => go(t.path)}>{t.label}</button>
        ))}
      </div>

      {/* ── Sito web ── */}
      {tab === 'siti' && (
        loadingH ? <div className="nfx-card"><div className="nfx-loading">Caricamento…</div></div>
          : hostList.length === 0 ? (
            <IlluEmpty title="Non hai nessun sito web"
              desc="Crea un sito con il nostro Site Creator o installa WordPress in pochi clic."
              primary="Aggiungere un sito" link="Scopri le nostre offerte"
              onPrimary={() => offer()} onLink={offer} />
          ) : (
            <div className="nfx-card">
              <div className="nfx-tb">
                <span className="nfx-tb__count"><b>{hostList.length}</b> {hostList.length === 1 ? 'Sito web' : 'Siti web'}</span>
                <button className="nfx-tb__btn" onClick={() => offer()}>Aggiungere un sito</button>
                <span className="nfx-tb__spacer" />{rightIcons}
              </div>
              <table className="nfx-table">
                <thead><tr><th className="nfx-td-ico" /><th>Nome</th><th>Certificato SSL</th><th>Applicazioni</th><th>Ambienti</th><th>Prodotto correlato</th><th>Tag</th><th className="nfx-td-actions" /></tr></thead>
                <tbody>
                  {hostList.map((h, i) => (
                    <tr key={h.id || i} className="nfx-row-click" onClick={() => go(`/hosting-domains/hosting/${h.id}`)}>
                      <td className="nfx-td-ico"><NavIcon name="nav-site-creator" size={26} /></td>
                      <td className="nfx-cell-strong">{h.dominio_principale || '—'}</td>
                      <td><span className="nfx-ssl-free"><Lock size={15} /> Free</span></td>
                      <td><NavIcon name={appIcon(h)} size={22} /></td>
                      <td>{h.php_version ? <span className="nfx-env-badge">PHP {h.php_version}</span> : '—'}</td>
                      <td><span className="nfx-cell-inline"><NavIcon name="nav-hosting-web" size={20} />{h.dominio_principale || h.piano || '—'}</span></td>
                      <td><button className="nfx-iconbtn" onClick={e => e.stopPropagation()}><Plus size={16} /></button></td>
                      <td className="nfx-td-actions"><button className="nfx-iconbtn" onClick={e => e.stopPropagation()}><MoreVertical size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}

      {/* ── Hosting ── */}
      {tab === 'hosting' && (
        loadingH ? <div className="nfx-card"><div className="nfx-loading">Caricamento…</div></div>
          : hostList.length === 0 ? (
            <IlluEmpty title="Non hai nessun hosting"
              desc="Ospita il tuo sito web con un piano hosting NeoFlux ad alte prestazioni."
              primary="Ordina un hosting" link="Scopri le nostre offerte" onPrimary={offer} onLink={offer} />
          ) : (
            <div className="nfx-card">
              <div className="nfx-tb">
                <span className="nfx-tb__count"><b>{hostList.length}</b> {hostList.length === 1 ? 'Hosting' : 'Hosting'}</span>
                <button className="nfx-tb__btn" onClick={offer}><ShoppingCart size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />Ordinare</button>
                <button className="nfx-tb__btn nfx-tb__btn--ghost">Gestire <ChevronDown size={14} style={{ verticalAlign: '-2px' }} /></button>
                <span className="nfx-tb__spacer" />{rightIcons}
              </div>
              <table className="nfx-table">
                <thead><tr><th className="nfx-td-ico" /><th>Nome</th><th>Prodotti correlati</th><th>Scadenza</th><th>Tag</th><th className="nfx-td-actions" /></tr></thead>
                <tbody>
                  {hostList.map((h, i) => (
                    <tr key={h.id || i} className="nfx-row-click" onClick={() => go(`/hosting-domains/hosting/${h.id}`)}>
                      <td className="nfx-td-ico"><NavIcon name="nav-hosting-web" size={26} /></td>
                      <td><div className="nfx-cell-strong">{h.dominio_principale || 'Hosting Web'}</div><div className="nfx-cell-sub">{h.piano || 'Hosting Web'}</div></td>
                      <td className="nfx-cell-sub" style={{ marginTop: 0 }}>—</td>
                      <td>{h.data_scadenza ? `Scade il ${fmtDate(h.data_scadenza)}` : '—'}</td>
                      <td><button className="nfx-iconbtn" onClick={e => e.stopPropagation()}><Plus size={16} /></button></td>
                      <td className="nfx-td-actions"><button className="nfx-iconbtn" onClick={e => e.stopPropagation()}><MoreVertical size={16} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
      )}

      {/* ── Domini ── */}
      {tab === 'domains' && (
        loadingD ? <div className="nfx-card"><div className="nfx-loading">Caricamento…</div></div>
          : domList.length === 0 ? (
            <IlluEmpty title="Non hai nessun dominio"
              desc="Inizia ordinando o trasferendo il dominio perfetto per il tuo progetto."
              primary="Scopri le nostre offerte" link="Ordina o trasferisci un dominio" onPrimary={offer} onLink={offer} />
          ) : (
            <div className="nfx-card">
              <div className="nfx-tb">
                <span className="nfx-tb__count"><b>{domList.length}</b> {domList.length === 1 ? 'Dominio' : 'Domini'}</span>
                <button className="nfx-btn-blue" onClick={offer}><ShoppingCart size={15} style={{ verticalAlign: '-2px', marginRight: 6 }} />Ordina</button>
                <button className="nfx-tb__btn nfx-tb__btn--ghost">Gestisci <ChevronDown size={14} style={{ verticalAlign: '-2px' }} /></button>
                <span className="nfx-tb__spacer" />{rightIcons}
              </div>
              {batchMsg && <div className={`nfx-dom-flash nfx-dom-flash--${batchMsg.type}`} style={{ margin: '0 16px 12px' }}>{batchMsg.text}</div>}
              <table className="nfx-table nfx-domtable">
                <thead><tr>
                  <th className="nfx-th-check"><input type="checkbox" className="nfx-check" checked={allSel} onChange={toggleAllDom} /></th>
                  <th><span className="nfx-th-sort">Domini <ArrowUpDown size={13} /></span></th>
                  <th><span className="nfx-th-sort">Titolare <ArrowUpDown size={13} /></span></th>
                  <th><span className="nfx-th-sort">Scadenza <ArrowUpDown size={13} /></span></th>
                  <th>Prodotti collegati</th>
                  <th>Opzioni</th>
                  <th>Tag</th>
                  <th className="nfx-td-actions" />
                </tr></thead>
                <tbody>
                  {domList.map((d, i) => {
                    const dm = dMinus(d.scadenza);
                    const sel = selDom.has(d.id);
                    return (
                      <tr key={d.id || i} className={`nfx-row-click${sel ? ' is-sel' : ''}`} onClick={() => go(`/hosting-domains/domains/${encodeURIComponent(d.nome)}`)}>
                        <td className="nfx-td-check" onClick={(e) => e.stopPropagation()}><input type="checkbox" className="nfx-check" checked={sel} onChange={() => toggleSel(d.id)} /></td>
                        <td><span className="nfx-cell-inline"><NavIcon name="nav-domini" size={20} /><span className="nfx-cell-strong">{d.nome}</span></span></td>
                        <td className="nfx-cell-muted">{owner}</td>
                        <td className="nfx-cell-muted">{fmtDate(d.scadenza)}{dm != null ? <span className="nfx-dom-dminus"> (D-{dm})</span> : ''}</td>
                        <td>{d.hosting_collegato_id
                          ? <span className="nfx-dom-prodic" title="Hosting Web"><NavIcon name="nav-hosting-web" size={18} /></span>
                          : <span className="nfx-cell-muted">—</span>}</td>
                        <td>{d.id_protection
                          ? <span className="nfx-dom-optic" title="Protezione privacy attiva"><ShieldCheck size={16} /></span>
                          : <span className="nfx-cell-muted">—</span>}</td>
                        <td onClick={(e) => e.stopPropagation()}><button className="nfx-dom-tagadd" title="Aggiungi un tag"><Plus size={15} /></button></td>
                        <td className="nfx-td-actions" onClick={(e) => e.stopPropagation()}><button className="nfx-iconbtn"><MoreVertical size={16} /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {selDom.size > 0 && (
                <div className="nfx-batchbar">
                  <div className="nfx-batchbar__l">
                    <div className="nfx-batchbar__t">Elaborazione in blocco</div>
                    <div className="nfx-batchbar__n">{selDom.size} {selDom.size === 1 ? 'dominio selezionato' : 'domini selezionati'}</div>
                  </div>
                  <button className="nfx-batchbar__act is-disabled" disabled><ArrowRight size={16} /> Ottieni codici di trasferimento</button>
                  <button className="nfx-batchbar__act is-disabled" disabled><ArrowRight size={16} /> Trasferisci a NeoFlux</button>
                  <button className="nfx-batchbar__act is-disabled" disabled><Users2 size={16} /> Cambia titolare</button>
                  <button className="nfx-batchbar__act" onClick={() => { setNsForm({ ns1: '', ns2: '', ns3: '', ns4: '', ns5: '' }); setNsModal(true); }} disabled={!!batchBusy}><RotateCcw size={16} /> {batchBusy === 'ns' ? 'Applico…' : 'Modifica server DNS'}</button>
                  <button className="nfx-batchbar__act" onClick={batchLock} disabled={!!batchBusy}><Lock size={16} /> {batchBusy === 'lock' ? 'Blocco…' : 'Blocca'}</button>
                </div>
              )}

              {nsModal && (
                <div className="nfx-hmodal-scrim" onClick={() => setNsModal(false)}>
                  <div className="nfx-hmodal" onClick={(e) => e.stopPropagation()}>
                    <div className="nfx-hmodal__title">Modifica i server DNS ({selDom.size} {selDom.size === 1 ? 'dominio' : 'domini'})</div>
                    <p className="nfx-dom-note">I nuovi server DNS verranno applicati a tutti i domini selezionati.</p>
                    {['ns1', 'ns2', 'ns3', 'ns4', 'ns5'].map((k, i) => (
                      <input key={k} className="nfx-dom-nsinput" style={{ width: '100%', boxSizing: 'border-box', marginBottom: 10 }}
                        placeholder={`Server DNS ${i + 1}${i < 2 ? ' *' : ''}`} value={nsForm[k]}
                        onChange={(e) => setNsForm((f) => ({ ...f, [k]: e.target.value.trim() }))} />
                    ))}
                    <div className="nfx-dom-nsbtns" style={{ justifyContent: 'flex-end' }}>
                      <button className="nfx-btn-sec" onClick={() => setNsModal(false)}>Annulla</button>
                      <button className="nfx-btn-pri" onClick={batchNs}>Applica</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
      )}

      {/* ── Certificato SSL ── */}
      {tab === 'ssl' && (
        <IlluEmpty title="Aggiungi certificato"
          desc="Proteggi e autentica i tuoi siti e domini aggiungendo il tuo primo certificato SSL."
          primary="Scopri le nostre offerte" link="Aggiungi certificato" onPrimary={offer} onLink={offer} />
      )}
    </div>
  );
};

export const SitiPage = () => <HostingDashboard tab="siti" />;
export const HostingListPage = () => <HostingDashboard tab="hosting" />;
export const DominiPage = () => <HostingDashboard tab="domains" />;
export const SslPage = () => <HostingDashboard tab="ssl" />;

/* ═══════════════════ DETTAGLIO DOMINIO ═══════════════════ */
export const DomainDetailPage = () => {
  const navigate = useNavigate();
  const { nome } = useParams();
  const { utente } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const sec = searchParams.get('sec') || 'panel';
  const goSec = (s) => { if (s === 'panel') setSearchParams({}); else setSearchParams({ sec: s }); };
  const [dom, loading] = useFetch(`/api/domini/${encodeURIComponent(nome)}`, null);
  const [nsMode, setNsMode] = useState('custom');
  const [gestOpen, setGestOpen] = useState(false);
  const [autoOv, setAutoOv] = useState(null);
  const [lockOv, setLockOv] = useState(null);
  const [protOv, setProtOv] = useState(null);
  const [dnssecOv, setDnssecOv] = useState(null);
  const [nsOv, setNsOv] = useState(null);
  const [showEpp, setShowEpp] = useState(false);
  const [eppVal, setEppVal] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null); // { type:'ok'|'err', text }
  const [nsEdit, setNsEdit] = useState(false);
  const [nsForm, setNsForm] = useState({ ns1: '', ns2: '', ns3: '', ns4: '', ns5: '' });

  if (loading) return <div className="nfx-card" style={{ marginTop: 8 }}><div className="nfx-loading">Caricamento…</div></div>;
  if (!dom || dom.errore) return <div className="nfx-card nfx-card--pad" style={{ marginTop: 8 }}>Dominio non trovato.</div>;

  const d = dom;
  const enc = encodeURIComponent(d.nome);
  const st = STATE_TAG(d.stato);
  const auto = autoOv !== null ? autoOv : !!d.auto_rinnovo;
  const prot = protOv !== null ? protOv : !!d.id_protection;
  const dnssec = dnssecOv !== null ? dnssecOv : !!d.dnssec_attivo;
  const baseLocked = /lock/i.test(d.lock_status || '') && !/unlock/i.test(d.lock_status || '');
  const locked = lockOv !== null ? lockOv : baseLocked;
  const ns = nsOv !== null ? nsOv : [d.nameserver1, d.nameserver2, d.nameserver3, d.nameserver4, d.nameserver5].filter(Boolean);
  let whois = d.whois_data;
  if (typeof whois === 'string') { try { whois = JSON.parse(whois); } catch { whois = null; } }
  whois = whois || {};
  const wget = (...keys) => {
    for (const k of keys) for (const wk of Object.keys(whois)) if (wk.toLowerCase() === k.toLowerCase() && whois[wk]) return whois[wk];
    return null;
  };
  const hasWhois = ['name', 'registrant', 'fullname', 'organization', 'email', 'address', 'city'].some(k => wget(k));
  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4500); };
  const errText = (e) => e?.response?.data?.messaggio || 'Operazione non riuscita sul registrar';

  const toggleAuto = () => {
    const nv = !auto; setAutoOv(nv);
    api.patch(`/api/domini/${enc}`, { auto_rinnovo: nv }).catch(() => { setAutoOv(!nv); flash('err', 'Impossibile aggiornare il rinnovo automatico'); });
  };
  const toggleProt = () => {
    if (busy) return; const nv = !prot; setBusy('prot'); setProtOv(nv);
    api.put(`/api/domini/${enc}/id-protection`, { enabled: nv })
      .then(() => flash('ok', `Privacy WHOIS ${nv ? 'attivata' : 'disattivata'}.`))
      .catch((e) => { setProtOv(!nv); flash('err', errText(e)); }).finally(() => setBusy(''));
  };
  const toggleDnssec = () => {
    if (busy) return; const nv = !dnssec; setBusy('dnssec'); setDnssecOv(nv);
    api.post(`/api/domini/${enc}/dnssec`, { enabled: nv })
      .then(() => flash('ok', `DNSSEC ${nv ? 'attivato' : 'disattivato'}.`))
      .catch((e) => { setDnssecOv(!nv); flash('err', errText(e)); }).finally(() => setBusy(''));
  };
  const toggleLock = () => {
    if (busy) return; const nv = !locked; setBusy('lock');
    api.put(`/api/domini/${enc}/lock`, { locked: nv })
      .then((r) => { const s = r.data?.lock_status || (nv ? 'locked' : 'unlocked'); setLockOv(/lock/i.test(s) && !/unlock/i.test(s)); flash('ok', `Dominio ${nv ? 'bloccato' : 'sbloccato'}.`); })
      .catch((e) => flash('err', errText(e))).finally(() => setBusy(''));
  };
  const revealEpp = () => {
    if (showEpp) { setShowEpp(false); return; }
    if (busy) return; setBusy('epp');
    api.get(`/api/domini/${enc}/epp`)
      .then((r) => { setEppVal(r.data?.epp || 'Non disponibile'); setShowEpp(true); })
      .catch((e) => flash('err', errText(e))).finally(() => setBusy(''));
  };
  const renew = () => {
    if (busy) return; setBusy('renew');
    api.post(`/api/domini/${enc}/renew`, { anni: 1 })
      .then(() => flash('ok', 'Richiesta di rinnovo inviata al registrar.'))
      .catch((e) => flash('err', errText(e))).finally(() => setBusy(''));
  };
  const startNsEdit = () => { setNsForm({ ns1: d.nameserver1 || '', ns2: d.nameserver2 || '', ns3: d.nameserver3 || '', ns4: d.nameserver4 || '', ns5: d.nameserver5 || '' }); setNsEdit(true); };
  const saveNs = () => {
    if (!nsForm.ns1 || !nsForm.ns2) { flash('err', 'Inserisci almeno i primi due server DNS'); return; }
    setBusy('ns');
    api.put(`/api/domini/${enc}/nameservers`, nsForm)
      .then(() => { setNsOv([nsForm.ns1, nsForm.ns2, nsForm.ns3, nsForm.ns4, nsForm.ns5].filter(Boolean)); setNsEdit(false); flash('ok', 'Server DNS aggiornati.'); })
      .catch((e) => flash('err', errText(e))).finally(() => setBusy(''));
  };

  const owner = (() => {
    if (!utente) return '—';
    const nc = [utente.nome, utente.cognome].filter(Boolean).join(' ').trim();
    if (utente.tipo_cliente === 'azienda' && utente.ragione_sociale) {
      return nc ? `${nc}, ${utente.ragione_sociale}` : utente.ragione_sociale;
    }
    return nc || utente.email || '—';
  })();
  const SEC_TITLE = {
    panel: 'Pannello di controllo', ns: 'Server DNS', dns: 'Zona DNS', whois: 'Dettagli Whois',
    transfer: 'Trasferimento', redir: 'Reindirizzamenti web', synonym: 'Domini sinonimo',
    dyndns: 'DNS dinamico', connect: 'Collega un servizio',
  };

  const Row = ({ label, children }) => (
    <div className="nfx-dom-row"><div className="nfx-dom-row__k">{label}</div><div className="nfx-dom-row__v">{children}</div></div>
  );
  const Tgl = ({ on, onClick, k }) => (
    <span className="nfx-dom-toggle">
      <button className={`nfx-toggle${on ? ' is-on' : ''}`} onClick={onClick} aria-pressed={on} type="button" disabled={busy === k} />
      <span>{on ? 'Attivo' : 'Disattivato'}</span>
    </span>
  );

  return (
    <div>
      <div className="nfx-crumb">
        <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE)} aria-label="Home"><Home size={16} /></button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE + '/hosting-domains/domains')} style={{ fontSize: 13.5, width: 'auto' }}>Domini</button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <span className="nfx-crumb__cur">{d.nome}</span>
      </div>

      {/* Intestazione (stile pagina hosting) */}
      <div className="nfx-hdd-head">
        <span className="nfx-hdd-head__ic" style={{ background: '#e9f0ff', color: 'var(--nfx-blue)' }}><Globe size={28} /></span>
        <div style={{ flex: 1 }}>
          <div className="nfx-hdd-head__t">
            <span className="nfx-hdd-head__name">{d.nome}</span>
            <span className="nfx-hdd-tag"><Plus size={12} style={{ marginRight: 3, verticalAlign: '-1px' }} />Aggiungere un tag</span>
            <span className={`nfx-st nfx-st--${st.cls}`}>{st.label}</span>
          </div>
          <div className="nfx-hdd-head__sub">Titolare: {owner} · Scade il {fmtDate(d.scadenza)}</div>
        </div>
        <div className="nfx-cp-gest">
          <button className="nfx-btn-blue" onClick={() => setGestOpen((o) => !o)}>Gestisci <ChevronDown size={15} /></button>
          {gestOpen && (
            <div className="nfx-cp-gest__menu" onMouseLeave={() => setGestOpen(false)}>
              <button onClick={() => { setGestOpen(false); renew(); }} disabled={busy === 'renew'}><RotateCcw size={15} /> {busy === 'renew' ? 'Rinnovo…' : 'Rinnova il dominio'}</button>
              <button onClick={() => { setGestOpen(false); goSec('ns'); }}><Server size={15} /> Modifica i server DNS</button>
              <button onClick={() => { setGestOpen(false); goSec('transfer'); }}><ArrowRight size={15} /> Trasferisci il dominio</button>
            </div>
          )}
        </div>
      </div>

      {msg && <div className={`nfx-dom-flash nfx-dom-flash--${msg.type}`} style={{ marginBottom: 20 }}>{msg.text}</div>}

      {/* ══════════ Pannello di controllo ══════════ */}
      {sec === 'panel' && (
        <>
          <div className="nfx-hdd-secthead">
            <div className="nfx-hdd-section-title" style={{ margin: 0 }}><Layers size={18} /> Servizi collegati</div>
            <button className="nfx-btn-blue is-disabled" disabled><Plus size={15} /> Collega un servizio</button>
          </div>
          <div className="nfx-card" style={{ marginBottom: 26, overflow: 'hidden' }}>
            <table className="nfx-dm-svc">
              <thead><tr><th>Dominio / sottodominio</th><th>Email</th><th>Web e Cloud</th><th>Altro</th></tr></thead>
              <tbody>
                <tr>
                  <td><div className="nfx-dm-svc__dom">{d.nome}</div><div className="nfx-dm-svc__tag">dominio primario</div></td>
                  <td className="nfx-dm-svc__c">—</td>
                  <td className="nfx-dm-svc__c">{d.hosting_collegato_id ? <span className="nfx-dm-svc__ic" title="Hosting Web"><Server size={16} /></span> : '—'}</td>
                  <td className="nfx-dm-svc__c">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="nfx-dm-grid">
            <div className="nfx-card nfx-card--pad">
              <div className="nfx-hdd-cardtitle">Opzioni</div>
              <div className="nfx-cp-opts">
                <div className="nfx-cp-opt">
                  <div className="nfx-cp-opt__h"><RotateCcw size={18} /><span>Rinnovo automatico</span></div>
                  <p>Rinnova automaticamente il dominio prima della scadenza.</p>
                  <Tgl on={auto} onClick={toggleAuto} k="auto" />
                </div>
                <div className="nfx-cp-opt">
                  <div className="nfx-cp-opt__h"><ShieldCheck size={18} /><span>Privacy WHOIS</span></div>
                  <p>Nasconde i tuoi dati personali nella banca dati WHOIS pubblica.</p>
                  <Tgl on={prot} onClick={toggleProt} k="prot" />
                </div>
                <div className="nfx-cp-opt">
                  <div className="nfx-cp-opt__h"><Lock size={18} /><span>Blocco del trasferimento</span></div>
                  <p>Impedisce il trasferimento non autorizzato verso un altro registrar.</p>
                  <span className="nfx-dom-toggle">
                    <button className={`nfx-toggle${locked ? ' is-on' : ''}`} onClick={toggleLock} aria-pressed={locked} type="button" disabled={busy === 'lock'} />
                    <span>{locked ? 'Bloccato' : 'Sbloccato'}</span>
                  </span>
                </div>
                <div className="nfx-cp-opt">
                  <div className="nfx-cp-opt__h"><Database size={18} /><span>DNSSEC</span></div>
                  <p>Protegge la risoluzione DNS del dominio con firme crittografiche.</p>
                  <Tgl on={dnssec} onClick={toggleDnssec} k="dnssec" />
                </div>
              </div>
            </div>

            <div className="nfx-card nfx-card--pad">
              <div className="nfx-hdd-cardtitle">Azioni</div>
              <div className="nfx-hdd-actions">
                <button className="nfx-hdd-action" onClick={() => goSec('dns')}><Layers size={16} /> Cambia la zona DNS</button>
                <button className="nfx-hdd-action" onClick={() => goSec('ns')}><Server size={16} /> Modifica i server DNS</button>
                <button className="nfx-hdd-action" onClick={() => goSec('whois')}><Info size={16} /> Cambia titolare</button>
                <button className="nfx-hdd-action" onClick={() => goSec('synonym')}><Globe size={16} /> Aggiungi un dominio sinonimo</button>
                <button className="nfx-hdd-action" onClick={() => goSec('redir')}><ExternalLink size={16} /> Aggiungi un reindirizzamento web</button>
                <button className="nfx-hdd-action" onClick={renew} disabled={busy === 'renew'}><RotateCcw size={16} /> {busy === 'renew' ? 'Rinnovo…' : 'Rinnova il dominio'}</button>
              </div>
            </div>
          </div>

          <div className="nfx-hdd-section-title"><Info size={18} /> Informazioni sul dominio</div>
          <div className="nfx-card nfx-card--pad">
            <Row label="Stato"><span className={`nfx-st nfx-st--${st.cls}`}>{st.label}</span></Row>
            <Row label="Data di registrazione">{fmtDate(d.data_registrazione)}</Row>
            <Row label="Data di scadenza">
              <span className="nfx-dom-epp">{fmtDate(d.scadenza)}
                <button className="nfx-acc-link" onClick={renew} disabled={busy === 'renew'}>{busy === 'renew' ? 'Rinnovo…' : 'Rinnova adesso'}</button>
              </span>
            </Row>
            <Row label="Server DNS">{ns.length ? <span className="nfx-cell-mono">{ns[0]}{ns.length > 1 ? ` +${ns.length - 1}` : ''}</span> : '—'}</Row>
            <Row label="Servizio collegato">{d.hosting_collegato_id ? 'Hosting Web' : 'Nessuno'}</Row>
          </div>
        </>
      )}

      {/* ══════════ Server DNS ══════════ */}
      {sec === 'ns' && (
        <>
          <div className="nfx-hdd-section-title" style={{ marginTop: 0 }}><Server size={18} /> Server DNS (name server)</div>
          {nsEdit ? (
            <>
              <div className="nfx-dm-warn"><Info size={17} /><div>Questa sezione è riservata agli utenti esperti. Una gestione errata può causare malfunzionamenti del dominio e dei servizi collegati. Le modifiche sono applicate direttamente, ma la propagazione su Internet può richiedere diverse ore.</div></div>
              <div className="nfx-card nfx-card--pad">
                <div className="nfx-dm-radios">
                  <label className="nfx-dm-radio">
                    <input type="radio" name="nsmode" checked={nsMode === 'neoflux'} onChange={() => { setNsMode('neoflux'); setNsForm({ ns1: 'ns1.neoflux.it', ns2: 'ns2.neoflux.it', ns3: '', ns4: '', ns5: '' }); }} />
                    <span>Server DNS NeoFlux</span>
                  </label>
                  <label className="nfx-dm-radio">
                    <input type="radio" name="nsmode" checked={nsMode === 'custom'} onChange={() => setNsMode('custom')} />
                    <span>Server DNS personalizzati</span>
                  </label>
                </div>
                {nsMode === 'custom' && (
                  <div className="nfx-dm-nsfields">
                    {['ns1', 'ns2', 'ns3', 'ns4', 'ns5'].map((k, i) => (
                      <div className="nfx-dm-nsfield" key={k}>
                        <label>{i === 0 ? 'Server primario *' : i === 1 ? 'Server secondario *' : `Server DNS ${i + 1}`}</label>
                        <input value={nsForm[k]} onChange={(e) => setNsForm((f) => ({ ...f, [k]: e.target.value.trim() }))} placeholder="es. ns1.provider.com" />
                      </div>
                    ))}
                  </div>
                )}
                <div className="nfx-dm-btns">
                  <button className="nfx-btn-pri" onClick={saveNs} disabled={busy === 'ns'}>{busy === 'ns' ? 'Salvataggio…' : 'Conferma'}</button>
                  <button className="nfx-btn-sec" onClick={() => setNsEdit(false)} disabled={busy === 'ns'}>Annulla</button>
                </div>
              </div>
            </>
          ) : (
            <div className="nfx-card nfx-card--pad">
              {ns.length ? (
                <div className="nfx-dm-nsview">
                  {ns.map((n, idx) => (
                    <div key={idx} className="nfx-dm-nsview__row">
                      <span className="nfx-dm-nsview__k">{idx === 0 ? 'Server primario' : idx === 1 ? 'Server secondario' : `Server ${idx + 1}`}</span>
                      <span className="nfx-dm-nsview__v">{n}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="nfx-dom-note">Nessun server DNS configurato.</p>}
              <div className="nfx-dm-btns" style={{ marginTop: 20 }}>
                <button className="nfx-btn-blue" onClick={() => { setNsMode('custom'); startNsEdit(); }}>Modifica i server DNS</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════ Zona DNS ══════════ */}
      {sec === 'dns' && (
        <>
          <div className="nfx-hdd-section-title" style={{ marginTop: 0 }}><Layers size={18} /> Gestisci la zona DNS del dominio</div>
          <p className="nfx-dom-note" style={{ marginBottom: 16 }}>I record DNS servono per varie funzioni: indirizzare un dominio verso un sito web, configurare le email, verificarne l&rsquo;autenticità e altro ancora.</p>
          <div className="nfx-dm-warn"><Info size={17} /><div><b>Questa sezione è riservata agli utenti esperti.</b><br />Una gestione errata può causare malfunzionamenti del dominio e dei servizi collegati.</div></div>
          {ns.some((n) => /neoflux\.it$/i.test(n)) ? (
            <div className="nfx-card nfx-card--pad">
              <p className="nfx-dom-note">La zona DNS di questo dominio è gestita dai server DNS NeoFlux. La gestione dei singoli record (A, AAAA, MX, TXT, CNAME) sarà disponibile a breve da questa sezione.</p>
            </div>
          ) : (
            <div className="nfx-card nfx-card--pad">
              <p className="nfx-dom-note">La zona DNS non è gestita da NeoFlux perché il dominio usa server DNS esterni:</p>
              <div className="nfx-dm-nsview" style={{ marginTop: 10 }}>
                {ns.map((n, idx) => <div key={idx} className="nfx-dm-nsview__row"><span className="nfx-dm-nsview__k">Server {idx + 1}</span><span className="nfx-dm-nsview__v">{n}</span></div>)}
              </div>
              <div className="nfx-dm-btns" style={{ marginTop: 20 }}><button className="nfx-btn-blue" onClick={() => goSec('ns')}>Usa i server DNS NeoFlux</button></div>
            </div>
          )}
        </>
      )}

      {/* ══════════ Dettagli Whois ══════════ */}
      {sec === 'whois' && (
        <>
          <div className="nfx-hdd-section-title" style={{ marginTop: 0 }}><Info size={18} /> Titolare del dominio</div>
          <div className="nfx-card nfx-card--pad" style={{ marginBottom: 22 }}>
            {hasWhois ? (
              <div className="nfx-dm-contact">
                <div className="nfx-dm-contact__name">{wget('name', 'registrant', 'fullname', 'ownername') || owner}</div>
                {wget('organization', 'org', 'company') && <div>{wget('organization', 'org', 'company')}</div>}
                <div>{[wget('address', 'address1', 'street'), wget('postcode', 'zip', 'cap'), wget('city'), wget('country', 'countryname')].filter(Boolean).join(', ') || '—'}</div>
                <div>{wget('email') || '—'}</div>
                <div>{wget('phone', 'telephone', 'tel') || '—'}</div>
              </div>
            ) : (
              <p className="nfx-dom-note">{prot
                ? 'I dati di contatto sono protetti dalla privacy WHOIS.'
                : 'Dati di contatto non disponibili. Sincronizza il dominio o contatta l’assistenza.'}</p>
            )}
            <div className="nfx-dm-btns" style={{ marginTop: 18 }}>
              <button className="nfx-btn-blue is-disabled" disabled>Cambia titolare</button>
              <button className="nfx-btn-sec is-disabled" disabled>Modifica</button>
            </div>
          </div>
          <p className="nfx-dom-note">La modifica del titolare (trasferimento di proprietà) e dei contatti WHOIS va richiesta all&rsquo;assistenza NeoFlux.</p>
        </>
      )}

      {/* ══════════ Trasferimento ══════════ */}
      {sec === 'transfer' && (
        <>
          <div className="nfx-hdd-section-title" style={{ marginTop: 0 }}><ArrowRight size={18} /> Trasferimento del dominio</div>
          <div className="nfx-card nfx-card--pad">
            <Row label="Blocco registrar">
              <span className="nfx-dom-toggle">
                <button className={`nfx-toggle${locked ? ' is-on' : ''}`} onClick={toggleLock} aria-pressed={locked} type="button" disabled={busy === 'lock'} />
                <span>{locked ? 'Bloccato' : 'Sbloccato'}</span>
              </span>
            </Row>
            <Row label="Codice di autorizzazione (EPP)">
              <span className="nfx-dom-epp">
                <span className="nfx-cell-mono">{showEpp ? (eppVal || 'Non disponibile') : '••••••••'}</span>
                <button className="nfx-acc-link" onClick={revealEpp} disabled={busy === 'epp'}>{busy === 'epp' ? '…' : (showEpp ? 'Nascondi' : 'Mostra')}</button>
              </span>
            </Row>
            <p className="nfx-dom-note" style={{ marginTop: 14 }}>Per trasferire questo dominio verso un altro registrar, sbloccalo e fornisci il codice di autorizzazione. Per un trasferimento in entrata verso NeoFlux, contatta l&rsquo;assistenza.</p>
          </div>
        </>
      )}

      {/* ══════════ Sezioni non supportate dal registrar ══════════ */}
      {['redir', 'synonym', 'dyndns', 'connect'].includes(sec) && (
        <>
          <div className="nfx-hdd-section-title" style={{ marginTop: 0 }}>{SEC_TITLE[sec]}</div>
          <div className="nfx-card nfx-card--pad">
            <div className="nfx-cp-empty" style={{ border: 'none' }}>
              <img src={EMPTY_ILLU} alt="" style={{ width: 120, opacity: .9 }} />
              <p>Questa funzione non è ancora disponibile per i domini registrati con NeoFlux.<br />Sarà attivata prossimamente da questa sezione.</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ═══════════════════ DETTAGLIO HOSTING (Veduta d'insieme) ═══════════════════ */
export const HostingDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [hosting, loading] = useFetch('/api/hosting', []);
  const [usage] = useFetch(`/api/hosting/${id}/usage`, {});
  const offer = () => window.open('https://neoflux.it', '_blank');
  // SSO verso il pannello Webuzo dell'hosting
  const openWebuzo = () => window.open(`/api/hosting/${id}/webuzo-login`, '_blank', 'noopener');
  const list = Array.isArray(hosting) ? hosting : [];
  const h = list.find(x => String(x.id) === String(id)) || list[0] || {};
  const u = usage || {};
  const name = h.dominio_principale || 'Hosting Web';
  // Uso disco reale da Webuzo (byte) con fallback alle stringhe metadata
  const disk = u.disk;
  const hasDisk = disk && typeof disk.used === 'number';
  const discoU = hasDisk ? fmtBytes(disk.used) : (u.disco_str ?? h.disco_usato ?? '0 byte');
  const discoL = hasDisk ? (disk.limit == null ? 'illimitato' : fmtBytes(disk.limit)) : (u.disco_limite_str ?? h.disco_limite ?? '250 GB');
  const disco = `${discoU} / ${discoL} utilizzati`;
  const links = [
    { t: 'FTP/SSH', d: 'Aggiungere un utente', ic: FolderOpen, onClick: openWebuzo },
    { t: 'Database', d: 'Aggiungi un database', ic: Database, onClick: openWebuzo },
    { t: 'Restauro', d: 'Nessun salvataggio', ic: RotateCcw, onClick: openWebuzo },
    { t: 'Sicurezza', d: 'Nessun sito da analizzare', ic: ShieldCheck, disabled: true },
  ];

  if (loading) return <div className="nfx-card" style={{ marginTop: 8 }}><div className="nfx-loading">Caricamento…</div></div>;

  return (
    <div>
      {/* Breadcrumb */}
      <div className="nfx-crumb">
        <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE)} aria-label="Home"><Home size={16} /></button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE + '/hosting-domains/hosting')} style={{ fontSize: 13.5, width: 'auto' }}>Hosting</button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <span className="nfx-crumb__cur">{name}</span>
      </div>

      {/* Header */}
      <div className="nfx-hdd-head">
        <span className="nfx-hdd-head__ic"><Layers size={28} /></span>
        <div>
          <div className="nfx-hdd-head__t">
            <span className="nfx-hdd-head__name">{name}</span>
            <span className="nfx-hdd-tag">Aggiungere un tag</span>
          </div>
          <button className="nfx-hdd-manage" onClick={openWebuzo}>Gestire <ChevronDown size={15} /></button>
        </div>
      </div>

      {/* Top 3 colonne */}
      <div className="nfx-hdd-top">
        {/* Azioni rapide */}
        <div className="nfx-card nfx-card--pad">
          <div className="nfx-hdd-cardtitle">Azioni rapide</div>
          <div className="nfx-hdd-actions">
            <button className="nfx-hdd-action" onClick={openWebuzo}><FolderOpen size={16} /> Accedere al WebFTP <ExternalLink size={14} /></button>
            <button className="nfx-hdd-action" onClick={openWebuzo}><Database size={16} /> Accedere a PhpMyAdmin <ExternalLink size={14} /></button>
            <button className="nfx-hdd-action" onClick={openWebuzo}><RotateCcw size={16} /> Ripristinare il mio hosting</button>
            <button className="nfx-hdd-action" onClick={openWebuzo}><Info size={16} /> Attivare la manutenzione</button>
          </div>
        </div>

        {/* La mia offerta */}
        <div className="nfx-card nfx-card--pad">
          <div className="nfx-hdd-offer__head">
            <span className="nfx-hdd-offer__title"><Layers size={18} color="#2e4a3a" /> La mia offerta</span>
            <button className="nfx-acc-link" onClick={openWebuzo}>Gestire</button>
          </div>
          <div className="nfx-hdd-offer__sub">{h.piano || 'Hosting Web'}</div>
          <div className="nfx-hdd-offer__box">
            <div className="nfx-hdd-offer__row"><span className="nfx-hdd-offer__k"><LayoutTemplate size={16} /> Siti</span><span className="nfx-hdd-offer__v">0/20</span></div>
            <div className="nfx-hdd-offer__row"><span className="nfx-hdd-offer__k"><HardDrive size={16} /> Archiviazione</span><span className="nfx-hdd-offer__v">{disco}</span></div>
            <div className="nfx-hdd-offer__row"><span className="nfx-hdd-offer__k"><Calendar size={16} /> Rinnovo</span><span className="nfx-hdd-offer__v">{h.data_scadenza ? `Il ${fmtDate(h.data_scadenza)}` : '—'}</span></div>
            <div className="nfx-hdd-offer__row"><span className="nfx-hdd-offer__k"><Server size={16} /> Risorse</span><span className="nfx-hdd-offer__v">Condivise</span></div>
          </div>
        </div>

        {/* Link cards */}
        <div className="nfx-hdd-links">
          {links.map((l, i) => {
            const Icon = l.ic;
            return (
              <div key={i} className={`nfx-hdd-link${l.disabled ? ' is-disabled' : ''}`} onClick={l.disabled ? undefined : l.onClick}>
                <span className="nfx-hdd-link__ic"><Icon size={18} /></span>
                <div className="nfx-hdd-link__b"><div className="nfx-hdd-link__t">{l.t}</div><div className="nfx-hdd-link__d">{l.d}</div></div>
                {!l.disabled && <ChevronRight size={18} color="#9f9f9f" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Creare un primo sito web */}
      <div className="nfx-card">
        <div className="nfx-hdd-createsite">
          <div>
            <div className="nfx-hdd-createsite__t">Creare un primo sito web</div>
            <div className="nfx-hdd-createsite__d">Aggiungi un sito e scegli la configurazione o l'applicazione più adatta a te.</div>
            <button className="nfx-btn-blue" onClick={openWebuzo}>Aggiungere un sito</button>
          </div>
          <div className="nfx-hd-illu__illu"><img src={EMPTY_ILLU} alt="" /></div>
        </div>
      </div>

      {/* Ambienti */}
      <div className="nfx-hdd-section-title"><Layers size={18} /> Ambienti</div>
      <div className="nfx-hdd-env">
        <div className="nfx-hdd-envcard">
          <div className="nfx-hdd-envcard__head"><span className="nfx-hdd-envcard__name">Apache | PHP {h.php_version ? `${h.php_version}` : ''}</span></div>
          <div className="nfx-hdd-envrow"><span className="nfx-hdd-envrow__k"><HardDrive size={15} /> Archiviazione <b>{discoU} / {discoL}</b></span></div>
          <div className="nfx-hdd-envrow"><span className="nfx-hdd-envrow__k"><LayoutTemplate size={15} /> Sito <b>0 / 20</b></span><button className="nfx-iconbtn"><Plus size={16} /></button></div>
        </div>
        <div className="nfx-hdd-envcard">
          <div className="nfx-hdd-envcard__head"><span className="nfx-hdd-envcard__name"><Layers size={16} /> Node.js</span><span className="nfx-sub-badge" style={{ background: '#1d3c51' }}>Nuovo</span></div>
          <div className="nfx-hdd-createsite__d" style={{ marginBottom: 12 }}>Sfrutta le ultime tecnologie per i tuoi siti web in tutta facilità.</div>
          <button className="nfx-acc-link">Crea un sito con Node.js</button>
        </div>
      </div>

      {/* Maggiori informazioni */}
      <div className="nfx-hdd-section-title"><Info size={18} /> Maggiori informazioni</div>
      <div className="nfx-card nfx-hdd-info">
        <div><div className="nfx-hdd-info__k">Nome dell'host</div><div className="nfx-hdd-info__v">{h.server_hostname || 'Condiviso'}</div></div>
        <div style={{ gridColumn: 'span 2' }}><div className="nfx-hdd-info__k">Percorso assoluto</div><div className="nfx-hdd-info__v nfx-cell-mono">{h.username ? `/home/clients/${h.username}/` : '—'}</div></div>
        <div><div className="nfx-hdd-info__k">IPv4</div><div className="nfx-hdd-info__v">{h.ip || '—'}</div></div>
        <div><div className="nfx-hdd-info__k">IPv6</div><div className="nfx-hdd-info__v">—</div></div>
        <div><div className="nfx-hdd-info__k">IP dedicati</div><div className="nfx-hdd-info__v">0 / 0</div></div>
      </div>
    </div>
  );
};
